import React from "react";
import classnames from "classnames";
import { Link, NavLink, useLocation } from "react-router-dom";

import { Container } from "components/site/SitePrimitives";
import { navigationItems } from "content/siteContent";

function SiteNavbar() {
  const [collapseOpen, setCollapseOpen] = React.useState(false);
  const [openDropdown, setOpenDropdown] = React.useState(null);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  React.useEffect(() => {
    setCollapseOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const toggleNavbar = () => {
    setCollapseOpen((open) => {
      const next = !open;

      if (!next) {
        setOpenDropdown(null);
      }

      return next;
    });
  };

  const toggleDropdown = (label) => {
    setOpenDropdown((current) => (current === label ? null : label));
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  return (
    <>
      {collapseOpen ? (
        <button
          aria-label="Close navigation"
          className="site-nav-overlay"
          onClick={toggleNavbar}
          type="button"
        />
      ) : null}
      <header
        className={classnames("fixed-top", "site-navbar", {
          "site-navbar-scrolled": isScrolled || collapseOpen,
        })}
      >
        <Container className="site-navbar-inner">
          <div className="site-navbar-bar">
            <Link className="site-navbar-brand" to="/">
              <img
                alt="Ignitum Solutions"
                className="site-navbar-wordmark"
                src="/logos/IgnitumSolutions_RGB_Horizontal.svg"
              />
            </Link>
            <button
              aria-expanded={collapseOpen}
              aria-label="Toggle navigation"
              className="site-navbar-toggle"
              type="button"
              onClick={toggleNavbar}
            >
              <span className="site-navbar-toggle-bar" />
              <span className="site-navbar-toggle-bar" />
              <span className="site-navbar-toggle-bar" />
            </button>
          </div>

          <div
            className={classnames("site-nav-panel", collapseOpen && "is-open")}
          >
            <nav aria-label="Primary" className="site-nav-list">
              {navigationItems.map((item) => {
                if (item.children?.length) {
                  const isActive = item.children.some(
                    (child) =>
                      location.pathname === child.to ||
                      location.pathname.startsWith(`${child.to}/`)
                  );
                  const isOpen = openDropdown === item.label;

                  return (
                    <div
                      className={classnames(
                        "site-nav-item",
                        "site-nav-dropdown",
                        isActive && "is-active",
                        isOpen && "is-open"
                      )}
                      key={item.label}
                      onMouseEnter={() => setOpenDropdown(item.label)}
                      onMouseLeave={closeDropdown}
                    >
                      <button
                        aria-expanded={isOpen}
                        aria-haspopup="true"
                        className={classnames(
                          "site-nav-link",
                          "site-nav-dropdown-trigger",
                          isActive && "active"
                        )}
                        onClick={() => toggleDropdown(item.label)}
                        type="button"
                      >
                        <span>{item.navLabel || item.label}</span>
                        <span aria-hidden="true" className="site-nav-caret" />
                      </button>
                      <div className="site-nav-dropdown-menu">
                        {item.children.map((child) => (
                          <NavLink
                            className={({ isActive: childActive }) =>
                              classnames(
                                "site-nav-dropdown-link",
                                childActive && "active"
                              )
                            }
                            key={child.to}
                            to={child.to}
                          >
                            {child.navLabel || child.label}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="site-nav-item" key={item.to || item.label}>
                    <NavLink
                      className={({ isActive }) =>
                        classnames(
                          "site-nav-link",
                          item.cta && "site-nav-link-cta",
                          isActive && "active"
                        )
                      }
                      to={item.to}
                    >
                      {item.navLabel || item.label}
                    </NavLink>
                  </div>
                );
              })}
            </nav>
          </div>
        </Container>
      </header>
    </>
  );
}

export default SiteNavbar;
